import type { Locale } from '@saniplus/contracts';

/**
 * Everything the client runtime needs that only the host application knows:
 * where the API is, which language the UI is in, which device this is, and
 * where a server clock reading or an expired session should be routed.
 *
 * It is a single mutable holder rather than React context because the RTK
 * Query base query runs outside React entirely.
 */
export interface ApiClientRuntime {
  /** Origin + prefix, no trailing slash. `/api/v1` goes through the Vite proxy. */
  readonly baseUrl: string;
  /** UI locale, appended to every request as `?lang=` (decision-log amendment #20). */
  readonly getLocale: () => Locale;
  /** Device uuid provisioned in IndexedDB on first run (D-06). */
  readonly getDeviceUuid: () => string;
  /** User-agent string reported at login. */
  readonly getUserAgent: () => string;
  /** Clock anchor hook — called with `meta.serverTime` of every response (D-10). */
  readonly onServerTime: (serverTime: string) => void;
  /** Called once when a refresh fails and the session is unrecoverable. */
  readonly onSessionExpired: () => void;
  /**
   * IndexedDB name to keep the offline identity snapshot in, or `undefined`
   * — the default — to keep none at all (`auth/sessionSnapshot.ts`).
   *
   * This is the whole opt-in, and it is deliberately per app rather than a
   * global behaviour. The worker PWA names its database because an installed
   * app reopened inside a plant with no signal has to be able to paint its
   * shell and reach a queued punch. `apps/admin-web` names nothing: an admin
   * shell rendered from an unverified record is not a trade worth making, and
   * a supervisor at a desk always has a network.
   */
  readonly sessionSnapshotDb?: string;
}

const DEFAULTS: ApiClientRuntime = {
  baseUrl: '/api/v1',
  getLocale: () => 'fr',
  getDeviceUuid: () => '',
  getUserAgent: () => (typeof navigator === 'undefined' ? 'saniplus-client' : navigator.userAgent),
  onServerTime: () => undefined,
  onSessionExpired: () => undefined,
};

let current: ApiClientRuntime = { ...DEFAULTS };

/** Merge host configuration in. Safe to call more than once (locale changes). */
export function configureApiClient(partial: Partial<ApiClientRuntime>): void {
  current = { ...current, ...partial };
}

export function getApiClientRuntime(): ApiClientRuntime {
  return current;
}

/** Test-only: restore the defaults between cases. */
export function resetApiClientRuntime(): void {
  current = { ...DEFAULTS };
}
